import { useCallback, useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Pagination,
    Box,
    Snackbar,
    Alert
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import Loader from '../../components/loading/Loader';
import './SolicitudesCambio.css';
import { useAuth } from '../../context/AuthContext';

function obtenerNombreServicio(turno) {
    return turno.especialidad?.nombre ?? turno.practica?.nombre ?? '-';
}

export default function SolicitudesCambio() {

    const { user } = useAuth();
    const navigate = useNavigate();

    const rol = user?.rol;

    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);


    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [open, setOpen] = useState(false);
    const [mensaje, setMensaje] = useState("");
    const [tipo, setTipo] = useState("success");
    const limit = 10;

    const cargarSolicitudes = useCallback(async () => {
        try {
            setLoading(true);

            const res = await axiosInstance.get(
                `/turnos/solicitudes_cambio?page=${page}&limit=${limit}`
            );

            setSolicitudes(res.data.data);
            setTotalPages(res.data.paginacion.totalPaginas);

        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [page, limit]);


    async function aceptarSolicitud(turnoId) {
        try {
            await axiosInstance.patch(
                `/turnos/${turnoId}/cambio_fecha/confirmar`
            );
        
            setMensaje("Solicitud aprobada");
        
            setTipo("success");
        
            setOpen(true);

            cargarSolicitudes();
        } catch (err) {
            console.error(err);
        }
    }

    async function rechazarSolicitud(turnoId) {
        try {
            await axiosInstance.patch(
                `/turnos/${turnoId}/cambio_fecha/rechazar`,
                {
                    motivo: "Rechazo solicitud de cambio"
                }
            );

            setMensaje("Solicitud rechazada");
       
            setTipo("success");
       
            setOpen(true);
            
            cargarSolicitudes();
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        cargarSolicitudes();
    }, [cargarSolicitudes]);

    if (loading) return <Loader mensaje="Cargando solicitudes..." />;

    const columnaRol = rol === 'medico' ? 'Paciente' : 'Médico/a';

    return (
        <div className="solicitudes">

            <header className="solicitudes__header">
                <Button
                    className="solicitudes__volver"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/turnos')}
                >
                    Volver a Mis Turnos
                </Button>
                <span className="solicitudes__titulo">Solicitudes de Cambio</span>
            </header>

            <TableContainer component={Paper}>
                <Table
                    aria-label="Solicitudes de cambio"
                    sx={{
                        '& .MuiTableCell-root': {
                            py: 1.5,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" >{columnaRol}</TableCell>
                            <TableCell align="center">Especialidad/Práctica</TableCell>
                            <TableCell align="center">Fecha y Hora Turno</TableCell>
                            <TableCell align="center">Fecha y Hora Propuesta</TableCell>
                            <TableCell align="center">Acción</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {solicitudes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No hay solicitudes de cambio.
                                </TableCell>
                            </TableRow>
                        ) : (
                            solicitudes.map(turno => (
                                <TableRow key={turno._id} hover>
                                    <TableCell align="center" data-label={columnaRol}>
                                        {rol === 'medico'
                                            ? turno.paciente?.nombre
                                            : turno.medico?.nombre}
                                    </TableCell>

                                    <TableCell align="center" data-label="Especialidad/Práctica">
                                        {obtenerNombreServicio(turno)}
                                    </TableCell>

                                    <TableCell align="center" data-label="Fecha y Hora Turno">
                                        {new Date(turno.fechaHora).toLocaleString('es-AR')}
                                    </TableCell>

                                    <TableCell align="center" data-label="Fecha y Hora Propuesta">
                                        {new Date(turno.solicitudCambioFecha.fechaPropuesta)
                                            .toLocaleString('es-AR')}
                                    </TableCell>

                                    <TableCell align="center" data-label="Acción">
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Button
                                                className="accion-btn"
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                startIcon={<CheckIcon />}
                                                onClick={()=>aceptarSolicitud(turno._id)}
                                            >
                                                <span className="accion-btn__label">Aceptar</span>
                                            </Button>
                                            <Button
                                                className="accion-btn"
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                startIcon={<CloseIcon />}
                                                onClick={()=> rechazarSolicitud(turno._id)}
                                            >
                                                <span className="accion-btn__label">Rechazar</span>
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* PAGINACIÓN */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        sx={{
                            '& .MuiPaginationItem-root.Mui-selected': {
                                backgroundColor: 'var(--color-primary)',
                                color: '#fff',
                            },
                            '& .MuiPaginationItem-root.Mui-selected:hover': {
                                backgroundColor: '#078a87',
                            },
                        }}
                    />
                </Box>
            )}

            <Snackbar
                open={open}
                autoHideDuration={3000}
                onClose={() => setOpen(false)}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert
                    onClose={() => setOpen(false)}
                    severity={tipo}
                    variant="filled"
                >
                    {mensaje}
                </Alert>
            </Snackbar>

        </div>
    );
}
